import { InboxOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Empty, Input, Radio, Select, Space, Upload } from "antd";
import { useComponentDidUpdateEffect } from "common/customHooks";
import { showError } from "common/ToastNotifications";
import { FixWidthDataColumnSpecs, LoadingOverlay, SchemaAutoConfigureOptions, TextFilePreview } from "components";
import { DEFAULT_SAMPLE_SIZE } from "components/molecules/SchemaAutoConfigureOptions";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import { useRef, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Option } = Select;
const { Dragger } = Upload;

function SchemaModelCreateFromFile({
    onCreateSchemaModel,
    onUploadFile,
    uploadFileResult,
    uploadedFile,
    getParsedSchemaDefinition,
    getParsedSchemaDefinitionResult,
    schemaDefinition,
    onManageParsedSchemaModel,
    onCancelManageParsedSchemaModel,
    manageParsedSchemaModelCancelled,
    getSchemaProfile,
    getSchemaProfileResult,
    schemaProfile,
    schemaProfileUnsupported,
    onSchemaProfileReady,
    ...props
}) {
    let [fileReadResult, setFileReadResult] = useState({
        file: null,
        textContent: "",
        schemaFields: [],
        error: null,
    });

    const [busyStatus, setBusyStatus] = useState({ message: "", busy: false });
    const [firstRowLabel, setFirstRowLabel] = useState(true);
    const [schemaDetectOption, setSchemaDetectOption] = useState("AUTO_DETECT_STRUCTURE");
    const [fixWidthColumnSpecOption, setFixWidthColumnSpecOption] = useState("AUTO_DETECT_COL_SPECS");
    const [delimiterOption, setDelimiterOption] = useState("AUTO_DETECT");
    const [customDelimiter, setCustomDelimiter] = useState({ value: "", status: "" });
    const [showParsedPreview, setShowParsedPreview] = useState(false);
    const [fixedWidthColumSpecs, setFixedWidthColumSpecs] = useState([]);
    const [fixedWidthColumnSpecSelectedIndex, setFixedWidthColumnSpecSelectedIndex] = useState(-1);
    // PRE-2722: opt-in, default off. Never forced.
    const [autoConfigureEnabled, setAutoConfigureEnabled] = useState(false);
    const [sampleSize, setSampleSize] = useState(DEFAULT_SAMPLE_SIZE);


    const fileDraggerRef = useRef(null);

    /**
     * Eligibility gate (PRE-2722 AC1 / PRE-2718 AC5).
     *
     * A stacked schema definition declares structure rather than carrying data,
     * so there is nothing to sample. It is the CSV equivalent of a DDL, and the
     * parser service refuses to profile it. Hide the control instead of letting
     * the user enable something that will only degrade.
     */
    const canAutoConfigure = schemaDetectOption !== "SCHEMA_DEFINITION_STACKED";

    useComponentDidUpdateEffect(() => {
        if (fileReadResult.file) {
            if (uploadFileResult.success) {
                fileReader.readAsText(fileReadResult.file);
            } else {
                showError("Unable to generate preview");
                setBusyStatus({ message: "", busy: false });
            }
        }
    }, [uploadFileResult, uploadedFile]);

    useComponentDidUpdateEffect(() => {
        if (fileReadResult.file && uploadedFile) {
            if (getParsedSchemaDefinitionResult.success) {
                onDataParsingComplete(schemaDefinition);
            } else {
                showError("Unable to parse data file");
                setBusyStatus({ message: "", busy: false });
            }
        }
    }, [getParsedSchemaDefinitionResult, schemaDefinition]);

    useComponentDidUpdateEffect(() => {
        if (manageParsedSchemaModelCancelled) {
            setShowParsedPreview(false);
        }
    }, [manageParsedSchemaModelCancelled]);

    /**
     * A schema profile (or a refusal to sample) came back. Hand the pending
     * schema name to the container, which shows the review step (PRE-2722).
     */
    useComponentDidUpdateEffect(() => {
        if (!fileReadResult.file || !uploadedFile) return;
        if (!schemaProfile && !schemaProfileUnsupported) return;

        setBusyStatus({ message: "", busy: false });
        const filename = fileReadResult.file.name.replace(/\.[^/.]+$/, "");
        onSchemaProfileReady && onSchemaProfileReady(`${filename}_${Date.now()}`);
    }, [schemaProfile, schemaProfileUnsupported]);

    /**
     * Profiling failed outright. Do not strand the user on a spinner, and say
     * what happened: a silent fallback makes a backend failure look identical
     * to "no suggestions found".
     */
    useComponentDidUpdateEffect(() => {
        if (!fileReadResult.file || !uploadedFile) return;
        if (getSchemaProfileResult && getSchemaProfileResult.success === false) {
            showError(
                getSchemaProfileResult.message ||
                    "Could not analyse the sampled data. Continue without auto-configure, or try again.",
            );
            setBusyStatus({ message: "", busy: false });
        }
    }, [getSchemaProfileResult]);

    const onSchemaDetectOptionChange = (e) => {
        setSchemaDetectOption(e.target.value);
        setShowParsedPreview(false);
        onCancelManageParsedSchemaModel();
    };

    const onFixWidthColumnSpecOptionChange = (e) => {
        setFixWidthColumnSpecOption(e.target.value);
        setShowParsedPreview(false);
        onCancelManageParsedSchemaModel();
    };

    const onDelimiterOptionChange = (value) => {
        setDelimiterOption(value);
        setShowParsedPreview(false);
        onCancelManageParsedSchemaModel();
    };

    const onFirstRowLabelValueChanged = (e) => {
        setFirstRowLabel(e.target.checked);
        setShowParsedPreview(false);
        onCancelManageParsedSchemaModel();
    };

    const onUploadFileChange = async (file) => {
        fileReadResult = { ...fileReadResult };
        fileReadResult.file = file;
        setFileReadResult(fileReadResult);
        setBusyStatus({ message: "Preparing preview...", busy: true });
        onUploadFile(file);
    };

    const onTextContentReadComplete = () => {
        fileReadResult = { ...fileReadResult };
        fileReadResult.textContent = fileReader.result;
        setFileReadResult(fileReadResult);
        setBusyStatus({ message: "", busy: false });
    };

    const onTextContentReadError = () => {
        fileReadResult = { ...fileReadResult };
        fileReadResult.schemaFields = [];
        fileReadResult.error = fileReader.error;
        fileReadResult.textContent = "";
        setFileReadResult(fileReadResult);
        setBusyStatus({ message: "", busy: false });
    };

    const onFilePreviewClose = () => {
        fileReadResult = {
            file: null,
            schemaFields: [],
            error: null,
            textContent: "",
        };
        setFileReadResult(fileReadResult);
        setFirstRowLabel(false);
        setSchemaDetectOption("AUTO_DETECT_STRUCTURE");
        setFixWidthColumnSpecOption("AUTO_DETECT_COL_SPECS");
        setDelimiterOption("AUTO_DETECT");
        setCustomDelimiter({ value: "", status: "" });
        setShowParsedPreview(false);
        onCancelManageParsedSchemaModel();
        setFixedWidthColumSpecs([]);
        setFixedWidthColumnSpecSelectedIndex(-1);
        setAutoConfigureEnabled(false);
        setSampleSize(DEFAULT_SAMPLE_SIZE);
    };

    const onFileNext = () => {
        let delimiter = "";
        switch (delimiterOption) {
            case "COMMA":
                delimiter = ",";
                break;
            case "PIPE":
                delimiter = "|";
                break;
            case "TAB":
                delimiter = "\t";
                break;
            case "SEMI_COLON":
                delimiter = ";";
                break;
            case "CUSTOM":
                if (!customDelimiter.value) {
                    setCustomDelimiter({ ...customDelimiter, status: "error" });
                    return;
                }
                delimiter = customDelimiter.value;
                break;
            default:
                delimiter = delimiterOption;
                break;
        }

        const parseConfig = {
            dataFile: uploadedFile,
            firstRowLabel,
            schemaDetectOption,
            delimiter,
            fixWidthColumnDetectOption: fixWidthColumnSpecOption,
            fixWidthColumnSpecs: fixedWidthColumSpecs,
        };

        // PRE-2722: only sample and profile when the user opted in. Otherwise
        // this is byte-for-byte the previous unassisted flow.
        if (autoConfigureEnabled && canAutoConfigure && getSchemaProfile) {
            setBusyStatus({ message: `Sampling ${sampleSize} row(s) and analysing...`, busy: true });
            getSchemaProfile({ ...parseConfig, sampleSize });
            return;
        }

        setBusyStatus({ message: "Parsing data...", busy: true });
        getParsedSchemaDefinition(parseConfig);
    };

    const onDataParsingComplete = (schemaDefinition) => {
        if (schemaDefinition.schemaFields && schemaDefinition.schemaFields.length > 0) {
            setBusyStatus({ message: "", busy: false });
            const filename = fileReadResult.file.name.replace(/\.[^/.]+$/, "");
            setShowParsedPreview(true);
            onManageParsedSchemaModel(`${filename}_${Date.now()}`, schemaDefinition.schemaFields);
        } else {
            showError("No schema fields found.");
        }
    };

    const onAddFixWidthColumnSpec = ({ start, end, name }) => {
        const columnSpec = [...fixedWidthColumSpecs];
        columnSpec.push({ start, end, name });
        setFixedWidthColumSpecs(columnSpec);
    };

    const onInsertFixedWidthColumnSpec = ({ start, end, name }) => {
        if (fixedWidthColumnSpecSelectedIndex > -1) {
            const columnSpec = [...fixedWidthColumSpecs];
            columnSpec.splice(fixedWidthColumnSpecSelectedIndex, 0, { start, end, name });
            setFixedWidthColumSpecs(columnSpec);
        }
    };

    const onColumnSpecsChange = (columnSpecs) => {
        setFixedWidthColumSpecs(columnSpecs);
    };

    const onFixWidthSpecSelected = (selectedIndex) => {
        setFixedWidthColumnSpecSelectedIndex(selectedIndex);
    };

    const fileReader = new FileReader();
    fileReader.onload = onTextContentReadComplete;
    fileReader.onerror = onTextContentReadError;

    return (
        <>
            <LoadingOverlay busy={busyStatus.busy} spinner message={busyStatus.message}></LoadingOverlay>
            {fileReadResult.textContent ? (
                <FlexContainerLayout
                    showFooter={!showParsedPreview}
                    footer={
                        <div className="flex w-full flex-1 justify-end gap-4">
                            <Button onClick={onFilePreviewClose} data-testid={CypressTestIds.UPLOAD_SCHEMA_CANCEL_BUTTON}>
                                Cancel
                            </Button>
                            <Button type="primary" onClick={onFileNext} disabled={!uploadedFile} data-testid={CypressTestIds.UPLOAD_SCHEMA_NEXT_BUTTON}>
                                Next
                            </Button>
                        </div>
                    }
                >
                    <FlexContainerLayout
                        showFooter={true}
                        footer={
                            <div className="flex size-full flex-1 flex-col rounded-md bg-slate-50 p-4">
                                <div className="flex w-full gap-4">
                                    <div className="flex flex-col gap-3">
                                        <Checkbox checked={firstRowLabel} onChange={onFirstRowLabelValueChanged} data-testid={CypressTestIds.UPLOAD_SCHEMA_FIRST_ROW_CHECKBOX}>
                                            First row is column labels
                                        </Checkbox>
                                        <Radio.Group value={schemaDetectOption} onChange={onSchemaDetectOptionChange}>
                                            <Space orientation="vertical">
                                                <Radio value={"AUTO_DETECT_STRUCTURE"} data-testid={CypressTestIds.UPLOAD_SCHEMA_RADIO_AUTO_DETECT}>
                                                    Auto detect structure
                                                </Radio>
                                                <Radio value={"SCHEMA_DEFINITION_STACKED"} data-testid={CypressTestIds.UPLOAD_SCHEMA_RADIO_STACK_DEFINITION}>
                                                    Schema definition is stacked
                                                </Radio>
                                            </Space>
                                        </Radio.Group>
                                        {canAutoConfigure && (
                                            <SchemaAutoConfigureOptions
                                                enabled={autoConfigureEnabled}
                                                sampleSize={sampleSize}
                                                onEnabledChange={setAutoConfigureEnabled}
                                                onSampleSizeChange={setSampleSize}
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <Select
                                            style={{ width: 300 }}
                                            defaultValue={delimiterOption}
                                            onChange={onDelimiterOptionChange}
                                            data-testid={CypressTestIds.UPLOAD_SCHEMA_AUTODETECT_DELIMITER_DROPDOWN}
                                        >
                                            <Option value="AUTO_DETECT">Autodetect Delimiter</Option>
                                            <Option value="COMMA">Comma delimited</Option>
                                            <Option value="PIPE">Pipe delimited</Option>
                                            <Option value="TAB">tab delimited</Option>
                                            <Option value="SEMI_COLON">Semi-Colon delimited</Option>
                                            <Option value="FIX_WIDTH">Fix Width</Option>
                                            <Option value="CUSTOM">Custom character delimited</Option>
                                        </Select>
                                    </div>

                                    {delimiterOption === "CUSTOM" && (
                                        <div className="flex flex-col">
                                            <Input
                                                value={customDelimiter.value}
                                                status={customDelimiter.status}
                                                onChange={(e) => {
                                                    setCustomDelimiter({ value: e.target.value, status: "" });
                                                }}
                                                placeholder="Enter custom delimiter"
                                                data-testid={CypressTestIds.UPLOAD_SCHEMA_CUSTOM_DELIMITER_INPUT}
                                            />
                                        </div>
                                    )}
                                    {delimiterOption === "FIX_WIDTH" && (
                                        <div className="flex flex-col">
                                            <Radio.Group value={fixWidthColumnSpecOption} onChange={onFixWidthColumnSpecOptionChange}>
                                                <Space orientation="vertical">
                                                    <Radio value={"AUTO_DETECT_COL_SPECS"}>Auto detect columns</Radio>
                                                    <Radio value={"MANUAL_DEFINE_COL_SPECS"}>Manually define columns</Radio>
                                                </Space>
                                            </Radio.Group>
                                        </div>
                                    )}
                                </div>
                                {delimiterOption === "FIX_WIDTH" && fixWidthColumnSpecOption === "MANUAL_DEFINE_COL_SPECS" ? (
                                    <div className="flex flex-col gap-4">
                                        <Divider>Fix width column specs</Divider>
                                        {fixedWidthColumSpecs.length > 0 ? (
                                            <FixWidthDataColumnSpecs
                                                columnSpecs={fixedWidthColumSpecs}
                                                onColumnSpecsChange={onColumnSpecsChange}
                                                onSpecSelected={onFixWidthSpecSelected}
                                            ></FixWidthDataColumnSpecs>
                                        ) : (
                                            <Empty description="No column specs defined. Please select text and right click to add column spec."></Empty>
                                        )}
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        }
                    >
                        <TextFilePreview
                            content={fileReadResult.textContent}
                            enableColumnSpec={delimiterOption === "FIX_WIDTH" && fixWidthColumnSpecOption === "MANUAL_DEFINE_COL_SPECS"}
                            selectedColumnSpecIndex={fixedWidthColumnSpecSelectedIndex}
                            onInsertColumnSpec={onInsertFixedWidthColumnSpec}
                            onAddColumnSpec={onAddFixWidthColumnSpec}
                        ></TextFilePreview>
                    </FlexContainerLayout>
                </FlexContainerLayout>
            ) : (
                <Dragger
                    name="file"
                    multiple={false}
                    showUploadList={false}
                    action={onUploadFileChange}
                    ref={fileDraggerRef}
                    data-testid={CypressTestIds.UPLOAD_SCHEMA_FILE_INPUT}
                    className="h-full"
                    {...props}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    <p className="ant-upload-hint">Support for a singleupload.</p>
                </Dragger>
            )}
        </>
    );
}

export default SchemaModelCreateFromFile;
